import { ImageBlock, SlateBlock } from "@atlas/renderer";
import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  NextPage,
} from "next";
import Head from "next/head";
import Script from "next/script";
import React, { useEffect, useRef } from "react";
import { useMemo } from "react";
import ArticlesVotes from "../../../components/articles-votes";
import Content from "../../../components/content";
import LightLayout, {
  minTwoColumnScreenWidth,
} from "../../../components/layout.light";
import RelatedArticles from "../../../components/related-articles";
import formatDate from "../../../lib/format-date";
import loadArticle, { type Article } from "../../../lib/load-article";
import type { Section } from "../../../lib/load-categories";
import loadNestedCategoryArticles from "../../../lib/load-nested-category-articles";
import generalStyles from "../../../styles/general.module.css";
import styles from "../../../styles/article.module.css";
import type { AppSettings } from "../../../lib/get-app-settings";
import getBaseUrl from "../../../lib/get-base-url";
import { TAtlas_Identity } from "../../../lib/atlas-customer-context";
import { getAtlasIdentityFromRequest } from "../../../lib/atlas-customer-context/identify";
import {
  THCSessionDetails,
  getSessionDetailsFromRequest,
  saveSessionDetailsToLocalStorage,
} from "../../../lib/hc-session";
import trackSessionEvent from "../../../lib/hc-session";
import useRefCallback from "use-ref-callback";
import { useRouter } from "next/router";
import getPageUrl, { getDomain } from "../../../lib/get-page-url";
import getObjectByAlias from "../../../lib/get-object-by-alias";
import notFound from "../../../lib/not-found";
import Contents from "../../../components/contents";
import loadCustomer from "../../../lib/load-customer";
import { TCustomer } from "../../../models/customer";
import t from "../../../texts/t";
import window from "@atlas/tool/box/browser/window";
import type { THelpCenterLightLayout } from "../../../lib/load-company-settings";
import { useSearchParams } from "next/navigation";

export type TArticleLightPageProps = {
  appSettings: AppSettings;
  allArticles: Article[];
  allCategories: Section[];
  category: Section | null;
  article: Article;
  relatedArticles: Article[];
  atlasIdentity: TAtlas_Identity;
  trackedEventId: string | null;
  hcSessionDetails: THCSessionDetails;
  customerData: TCustomer | null;
  referer: string | null;
};

const ONE_SECOND = 1000;
const FIVE_SECONDS = 5 * ONE_SECOND;
const timeoutMultiplier = 1.2;
const maxTimeout = FIVE_SECONDS * 2;

const ArticleLightPage: NextPage<TArticleLightPageProps> = ({
  appSettings,
  allArticles,
  allCategories,
  category,
  article,
  relatedArticles,
  atlasIdentity,
  trackedEventId,
  hcSessionDetails,
  referer,
}) => {
  const router = useRouter();
  const pageUrl = router.asPath;

  const nodes = useMemo(
    () => (article.content ? JSON.parse(article.content) : []) as SlateBlock[],
    [article.content]
  );
  const baseUrl = useMemo(() => getBaseUrl(appSettings), [appSettings]);

  const enterTimeRef = useRef(Date.now());
  const delayRef = useRef(2);

  useEffect(() => {
    if (hcSessionDetails && appSettings) {
      saveSessionDetailsToLocalStorage(appSettings, hcSessionDetails);
    }
  }, [hcSessionDetails, appSettings]);

  const trackVisit = useRefCallback((force?: boolean) => {
    if (!atlasIdentity) return;
    if (!article) return;
    if (!trackedEventId) return;

    if (typeof window === "undefined" || (window.document.hidden && !force)) {
      return;
    }

    const timeSpent = Date.now() - enterTimeRef.current;

    trackSessionEvent(
      "VIEWED_ARTICLE",
      hcSessionDetails,
      pageUrl,
      [article],
      category ? [category] : [],
      {
        trackedEventId,
        timeSpent,
        referer,
      }
    );
  });

  // keep track of time spent on article
  // while the user is on the page
  // send data to custom events every 2 seconds
  // update the time spent on the article
  useEffect(
    function trackPageVisitMetrics() {
      delayRef.current = 2;
      enterTimeRef.current = Date.now();

      let timeout: NodeJS.Timeout;

      const nextTimeout = (t?: number) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          trackVisit(typeof t === "number");
          nextTimeout();
        }, (typeof t === "number" ? t : delayRef.current) * 1e3);

        delayRef.current = Math.min(
          delayRef.current * timeoutMultiplier,
          maxTimeout
        );
      };

      nextTimeout(0);

      const handleVisibilityChange = () => trackVisit(true);

      window?.document.addEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      return () => {
        trackVisit(true);
        clearTimeout(timeout);
        window?.document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    },
    [article.slug, trackVisit]
  );

  const hasCode = useMemo(
    () => nodes.some((node) => node.type === "code-block"),
    [nodes]
  );

  const title = article.pageTitle || article.title;
  const seoImageUrl = nodes.find<ImageBlock>(
    (node): node is ImageBlock => node.type === "image"
  )?.src;
  const description = article.description ?? "";
  const articleLocale = article.locale ?? "en_US";

  const sidebarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = () => {
      if (!window) return;
      if (sidebarRef.current?.parentElement) {
        sidebarRef.current.classList.toggle(
          styles.sticky,
          window?.innerWidth < minTwoColumnScreenWidth
            ? false
            : sidebarRef.current.parentElement.getBoundingClientRect().top <= 13 // Menu top padding
        );
      }
    };

    window?.addEventListener("scroll", handler);
    window?.addEventListener("resize", handler);

    return () => {
      window?.removeEventListener("scroll", handler);
      window?.removeEventListener("resize", handler);
    };
  }, []);

  const handleChangeActive = useRefCallback((activeId: string | null) => {
    if (!activeId) return;

    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const linkEl = sidebar.querySelector<HTMLAnchorElement>(
      `a[href="#${activeId}"]`
    );
    if (!linkEl) return;

    const rect = linkEl.getBoundingClientRect();

    const sidebarTop =
      rect.top < 0
        ? sidebar.scrollTop + rect.top - autoScrollGap
        : rect.top + rect.height > sidebar.offsetHeight
        ? sidebar.scrollTop +
          rect.top -
          sidebar.offsetHeight +
          rect.height +
          autoScrollGap
        : sidebar.scrollTop;
    if (sidebarTop === sidebar.scrollTop) return;

    sidebar.scrollTo({
      top:
        sidebarTop < 0
          ? 0
          : sidebarTop > sidebar.scrollHeight
          ? sidebar.scrollHeight
          : sidebarTop,
      behavior: "smooth",
    });
  });

  const searchValue = useSearchParams()?.get("query") || undefined;

  return (
    <LightLayout
      categories={allCategories}
      articles={allArticles}
      pageTitle={title}
      pageDescription={description}
      isCustomPageTitle={!!article.pageTitle}
      appSettings={appSettings}
      canonicalUrl={article.canonicalUrl}
      seoImageUrl={seoImageUrl}
      articleCategoryName={category?.title}
      articleLocale={articleLocale}
      searchValue={searchValue}
      sidebar={
        (appSettings.showTableOfContents && nodes.length) ||
        (appSettings.showRelatedArticles && relatedArticles.length) ? (
          <div ref={sidebarRef} className={styles.articleSidebar}>
            {appSettings.showTableOfContents && nodes.length ? (
              <Contents nodes={nodes} onChangeActive={handleChangeActive} />
            ) : null}
            {appSettings.showRelatedArticles && relatedArticles.length ? (
              <RelatedArticles
                className={styles.relatedArticles}
                articles={relatedArticles}
                baseUrl={baseUrl}
                language={appSettings.language}
                condensed
              />
            ) : null}
          </div>
        ) : null
      }
      selectedArticleSlug={article.slug}
    >
      <Head>
        {article.canonicalUrl && (
          <link rel="canonical" href={article.canonicalUrl} />
        )}
        {hasCode && (
          <link
            href="https://cdnjs.cloudflare.com/ajax/libs/prism/1.28.0/themes/prism-solarizedlight.min.css"
            rel="stylesheet"
          />
        )}
      </Head>

      {hasCode && (
        <Script src="https://cdnjs.cloudflare.com/ajax/libs/prism/1.28.0/prism.min.js" />
      )}

      <div itemScope itemType="https://schema.org/WebPage">
        <meta itemProp="name" content={article.title} />
        <div className={styles.articleContent}>
          <div itemProp="mainContentOfPage">
            <div
              className={generalStyles.articlePageArticle}
              itemScope
              itemType="https://schema.org/Article"
            >
              <h1 className={generalStyles.articlePageTitle} itemProp="name">
                {article.title}
              </h1>
              {article.lastPublishedAt && (
                <p
                  className={generalStyles.articlePageUpdatedDate}
                  lang={appSettings.language}
                >
                  {t("Last updated {0}", appSettings.language, [
                    formatDate(article.lastPublishedAt, appSettings.language),
                  ])}
                </p>
              )}
              <Content
                nodes={nodes}
                baseUrl={baseUrl}
                itemProp="text"
                defaultImageAlt={article.title}
              />
              <ArticlesVotes
                article={article}
                className={styles.articleVotes}
                hcSessionDetails={hcSessionDetails}
                visibility={appSettings.feedback}
                referer={referer}
                language={appSettings.language}
              />
            </div>
          </div>
        </div>
      </div>
    </LightLayout>
  );
};

export default ArticleLightPage;

const autoScrollGap = 30;

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
  appSettings: AppSettings & { layout: THelpCenterLightLayout }
): Promise<ReturnType<GetServerSideProps<TArticleLightPageProps>>> => {
  const referer = getDomain(context.req.headers.referer);
  const articleSlug = (context.params?.articleSlug || "").toString();
  const salt = context.query.preview?.toString();
  const isPreview = !!salt;

  try {
    const hcSessionDetails = await getSessionDetailsFromRequest(
      context,
      appSettings
    );

    const [allCategories = [], allArticles = [], article] = await Promise.all([
      [] as Section[],
      [] as Article[],
      // loadCategories(appSettings.slug, context),
      // loadAllArticles(appSettings.slug, context),
      loadArticle(appSettings.slug, articleSlug, salt, context),
    ]);

    if (!article) throw new Error(`Article not found: ${articleSlug}`);

    const category =
      allCategories.find((c) => c.id === article.categoryId) ?? null;

    const cookieKey = `atlasIdentity${appSettings.appId}`;
    const currentCustomerId = context.req.cookies[cookieKey];
    const customerData = currentCustomerId
      ? await loadCustomer(appSettings.appId, currentCustomerId).catch(
          (error) => {
            console.error(error);
            return null;
          }
        )
      : null;

    // If this is a preview, we don't need to load any related articles
    // or track any events
    if (isPreview && appSettings && article) {
      const atlasIdentity = getAtlasIdentityFromRequest(
        context.req,
        appSettings
      );
      return {
        props: {
          appSettings,
          allCategories,
          allArticles,
          category,
          article,
          relatedArticles: [],
          atlasIdentity,
          trackedEventId: null,
          hcSessionDetails,
          customerData,
          referer,
        },
      };
    }

    if (appSettings && article) {
      const atlasIdentity = getAtlasIdentityFromRequest(
        context.req,
        appSettings
      );

      const relatedArticles = category
        ? await loadNestedCategoryArticles(
            appSettings.slug,
            category.slug,
            5,
            context
          ).then(
            (articles) => articles?.filter((a) => a.id !== article.id) || []
          )
        : [];

      let trackedEventId: string | null = null;
      const trackedEvent = await trackSessionEvent(
        "VIEWED_ARTICLE",
        hcSessionDetails,
        getPageUrl(appSettings, context),
        [article],
        category ? [category] : [],
        {
          trackedEventId,
          timeSpent: 0,
          referer,
        }
      );
      if (trackedEvent && trackedEvent.id) {
        trackedEventId = trackedEvent.id as string;
      }
      const searchedQuery = context.query.SEARCHED_EVENT;

      let searchQueryStr = null;
      if (typeof searchedQuery === "string") {
        searchQueryStr = searchedQuery;
      } else if (Array.isArray(searchedQuery)) {
        searchQueryStr = searchedQuery.join(", ");
      }

      if (searchedQuery) {
        await trackSessionEvent(
          "SEARCHED_CLICKED",
          hcSessionDetails,
          getPageUrl(appSettings, context),
          [article],
          category ? [category] : [],
          { query: searchQueryStr, referer }
        );
      }

      return {
        props: {
          appSettings,
          allCategories,
          allArticles,
          category,
          article,
          relatedArticles,
          atlasIdentity,
          trackedEventId,
          hcSessionDetails,
          customerData,
          referer,
        },
      };
    }

    if (appSettings) {
      return notFound(appSettings);
    }
  } catch (error: any) {
    try {
      const object = await getObjectByAlias(
        appSettings.companyId,
        context.resolvedUrl,
        context
      );

      if (object) {
        return {
          redirect: {
            statusCode: 302,
            destination: object.path,
          },
        };
      }
    } catch (error: any) {
      // Fall through
    }
  }

  return notFound(appSettings);
};
